import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";

const passwordSchema = z.object({
  password: z.string().min(1, "Password is required."),
});

type PasswordFormValues = z.infer<typeof passwordSchema>;

interface PasswordEntryProps {
  email: string;
  onSubmit: (password: string) => void;
  onForgotPassword: () => void;
  onBack: () => void;
  loading: boolean;
  error: string | null;
}

export default function PasswordEntry({
  email,
  onSubmit,
  onForgotPassword,
  onBack,
  loading,
  error,
}: PasswordEntryProps) {
  const [showPassword, setShowPassword] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<PasswordFormValues>({
    resolver: zodResolver(passwordSchema),
  });

  const onFormSubmit = (data: PasswordFormValues) => {
    onSubmit(data.password);
  };

  return (
    <div>
      <button
        type="button"
        onClick={onBack}
        className="btn btn-link p-0 mb-4 text-muted d-flex align-items-center gap-1"
        aria-label="Go back"
      >
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <polyline points="15 18 9 12 15 6" />
        </svg>
        Back
      </button>

      <h1 className="h3 fw-bold text-dark mb-1">Enter your password</h1>
      <p className="text-muted mb-4">
        Signing in as <strong>{email}</strong>
      </p>

      <form onSubmit={handleSubmit(onFormSubmit)} noValidate>
        <div className="mb-3">
          <label htmlFor="password" className="form-label fw-medium">
            Password
          </label>
          <div className="input-group">
            <input
              id="password"
              type={showPassword ? "text" : "password"}
              autoComplete="current-password"
              autoFocus
              className={`form-control form-control-lg${
                errors.password ? " is-invalid" : ""
              }`}
              placeholder="Enter your password"
              disabled={loading}
              {...register("password")}
            />
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={() => setShowPassword((v) => !v)}
              aria-label={showPassword ? "Hide password" : "Show password"}
              tabIndex={-1}
            >
              {showPassword ? (
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
                  <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
                  <line x1="1" y1="1" x2="23" y2="23" />
                </svg>
              ) : (
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                  <circle cx="12" cy="12" r="3" />
                </svg>
              )}
            </button>
            {errors.password && (
              <div className="invalid-feedback">{errors.password.message}</div>
            )}
          </div>
        </div>

        {error && (
          <div className="alert alert-danger py-2 mb-3" role="alert">
            {error}
          </div>
        )}

        <button
          type="submit"
          className="btn btn-primary btn-lg w-100"
          disabled={loading}
        >
          {loading ? (
            <>
              <span
                className="spinner-border spinner-border-sm me-2"
                role="status"
                aria-hidden="true"
              />
              Signing in...
            </>
          ) : (
            "Sign In"
          )}
        </button>
        <div className="text-center mt-3">
          <button
            type="button"
            className="btn btn-link p-0 small text-muted"
            onClick={onForgotPassword}
          >
            Forgot password?
          </button>
        </div>
      </form>
    </div>
  );
}
